/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: Object component for ci.InsurableObject (update mode). Three navigation sections - the
 *         object data with its dynamic generic-template fields, the cr.CustomerInsurableObject
 *         assignments, and a conditional editable ad.Address for an object specific address.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';

import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsCrudService} from '../../../../../core/shared/service/crud/mvs-crud.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {DtoList} from '../../../../../core/shared/dto/dto.list';
import {DtoImportObjectContext} from '../../../../../core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from '../../../../../core/shared/dto/dto.import.object.context.entry';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {ObjectChangeInformation} from '../../../../../core/shared/logic/object-change-information';

import {InsurableObjectTypeDto} from '../../../model/dto/entity/insurable-object-type.dto';
import {InsurableObjectTypeService} from '../../../service/api/insurable-object-type.service';
import {
    CiInsurableObjectAddressFieldService
} from '../../../service/domain/ci-insurable-object-address-field.service';
import {
    CiInsurableObjectAddress,
    CiInsurableObjectAddressResolver
} from '../../../logic/ci-insurable-object-address';
import {CiCustomerInsurableObjectRequest} from '../../../logic/ci-customer-insurable-object-request';
import {
    CI_OBJECT_TYPE_CUSTOMER_INSURABLE_OBJECT
} from '../../../logic/ci-customer-insurable-object-assignment';
import {CI_ADDRESS_OBJECT_TYPE_ALIAS} from '../../../logic/ci-insurable-object-address-field';

// TYPE ONLY on purpose (gate condition GC1): cr already imports from ci, so a runtime import back
// into ci would close a ci -> cr -> ci cycle. `import type` is erased at compile time. Data access
// runs through MvsCoreService.getCrudService('cr.CustomerInsurableObject').
import type {CustomerInsurableObjectDto} from '../../../../cr/model/dto/entity/customer-insurable-object.dto';

/** cc.ObjectType alias of the customer, the entry the backend form scoping reads. */
const OBJECT_TYPE_CUSTOMER = 'cr.Customer';

/** German labels for cr.CustomerEntityAssignmentType, keyed by ordinal (main/secondary/info). */
const ASSIGNMENT_TYPE_LABELS: { [ordinal: number]: string } = {
    0: 'Hauptzuordnung',
    1: 'Nebenzuordnung',
    2: 'Info'
};

/**
 * Object component for {@code ci.InsurableObject}, update mode only.
 *
 * Registered through the NATIVE objectComponent registry of ci.entity-provider (gate condition
 * GC8), not through a service override: MvsCoreService.getObjectComponent consults the registry
 * first and only falls back to MvsCrudService.getObjectComponent. Deliberately registered for
 * mode=update ALONE (design section 15.5) - the four customer-less call sites that create an
 * insurable object keep the generic form.
 *
 * TAB MECHANISM (gate condition GC18): the shared {@code <object-component-navigation-side>} icon
 * rail, driven by native NavigationItems. Chosen over p-tabs because it is the platform's own
 * mechanism, it keeps the deep-link state in the sideNavItem query parameter, and it works in every
 * host - unlike the upward channel, which no standard object page renders. Whether that deep-link
 * state is kept at all is the host's decision, see {@link syncNavigationToUrl}.
 *
 * The items are deliberately NOT emitted through onNavigationItems, which is why the OBC checker
 * reports its G5.2 warning here: mvs-object/object.component.html renders its own
 * {@code <object-component-navigation-side>} as soon as navigationItems arrive, so emitting would
 * draw a SECOND rail around the one this template already shows. Same decision and same warning as
 * the reference implementation VtVaultStoreObjectComponent.
 *
 * Besides the object page this component is also hosted INLINE, as the edit strip underneath the
 * object list of {@code ci-customer-insurable-objects}. That host offers the strip as "Angaben
 * ergänzen" and therefore passes {@code params.removeDelete}, which this component honours by
 * dropping the delete action from ALL THREE of its forms - see {@link suppressDelete}. The same
 * host detaches the icon rail from the shared URL state, see {@link syncNavigationToUrl}.
 */
@Component({
    selector: 'ci-insurable-object-object',
    templateUrl: './ci-insurable-object-object.component.html',
    styleUrls: ['./ci-insurable-object-object.component.scss'],
    standalone: false
})
export class CiInsurableObjectObjectComponent
    extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    /** Sections of the icon rail. "Adresse" is appended only when it applies - see refreshAddress. */
    navigationItems: NavigationItem[] = [];

    /** The section currently shown; two-way bound by the shared rail. */
    activeNavigationItem: NavigationItem;

    /** The object's type, for the header icon and name. */
    type: InsurableObjectTypeDto;

    /** The cr.CustomerInsurableObject rows of this object - the "Kunde" section. */
    assignments: CustomerInsurableObjectDto[] = [];

    /** The assignment whose form is shown; preselected when there is exactly one. */
    selectedAssignment: CustomerInsurableObjectDto;

    /** Resolved address situation - decides whether the "Adresse" section exists at all. */
    address: CiInsurableObjectAddress;

    /** Context handed to the embedded forms so the backend scopes its value lists to the customer. */
    formImportObjectContext: DtoImportObjectContext;

    assignmentsBusy: boolean = false;
    assignmentsError: string;
    addressBusy: boolean = false;

    /** Rendered while the assignments load. */
    skeletonRows: number[] = [0, 1, 2];

    protected subscription: Subscription = new Subscription();

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        protected insurableObjectTypeService: InsurableObjectTypeService,
        protected addressFieldService: CiInsurableObjectAddressFieldService) {

        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.subscription.unsubscribe();
        super.ngOnDestroy();
    }

    /**
     * Method called whenever the object changed.
     *
     * NEVER calls refreshObject() - that would re-enter this hook forever.
     */
    onObjectChanged(): void {

        if (!this.dto) {
            return;
        }

        this.setPageTitle(this.getObjectLabel());

        this.rebuildNavigationItems();
        this.refreshType();
        this.loadAssignments();
    }

    // ---------------------------------------------------------------------------------------- //
    // Navigation
    // ---------------------------------------------------------------------------------------- //

    /**
     * Rebuild the rail.
     *
     * "Adresse" is appended only once the resolution proved the object carries an address of its
     * own; an object pointing at one of the customer's addresses is edited through the customer,
     * not here (design section 15.2).
     */
    protected rebuildNavigationItems(): void {

        const items: NavigationItem[] = [
            {
                label: 'Daten',
                action: 'data',
                icon: 'fa-regular fa-rectangle-list',
                tooltip: 'Daten des versicherbaren Objekts',
                toggleable: false,
                default: true
            },
            {
                label: 'Kunde',
                action: 'customer',
                icon: 'fa-regular fa-user',
                tooltip: 'Zuordnung des Objekts zu einem Kunden',
                toggleable: false,
                default: false
            }
        ];

        if (this.address?.objectSpecific) {
            items.push({
                label: this.address.addressField?.name || 'Adresse',
                action: 'address',
                icon: 'fa-regular fa-location-dot',
                tooltip: 'Objektspezifische Adresse dieses Objekts',
                toggleable: false,
                default: false
            });
        }

        this.navigationItems = items;

        if (!this.activeNavigationItem) {
            this.activeNavigationItem = items[0];
            return;
        }

        // The rail keeps a reference into the OLD array; re-point it so the active section survives
        // a rebuild (and falls back to the first one when its section just disappeared).
        const stillPresent = items.find(item => item.action === this.activeNavigationItem.action);
        this.activeNavigationItem = stillPresent ?? items[0];
    }

    // No explicit rail handler: the [(activeNavigationItem)] two-way binding already writes the
    // selection back. Whether the rail ALSO keeps the sideNavItem query parameter in sync is the
    // host's call - see syncNavigationToUrl.

    isSectionActive(action: string): boolean {
        return this.activeNavigationItem?.action === action;
    }

    // ---------------------------------------------------------------------------------------- //
    // Header
    // ---------------------------------------------------------------------------------------- //

    /**
     * The object's type, for the header icon. Read from the cached full type list rather than from
     * the DTO, because the resolved typeDto of a row does not reliably carry image/rgbaColor.
     */
    protected refreshType(): void {

        const typeId = this.dto?.['typeDtoId'];

        if (typeId == null) {
            this.type = null;
            return;
        }

        this.subscription.add(
            this.insurableObjectTypeService.listAllTypes().subscribe({
                next: (types: InsurableObjectTypeDto[]) =>
                    this.type = (types || []).find(entry => entry?.id === typeId),
                error: () => this.type = null
            })
        );
    }

    /** formatName || description || '<Typ> #<id>'. */
    getObjectLabel(): string {

        if (this.dto?.['formatName']) {
            return this.dto['formatName'];
        }

        if (this.dto?.['description']) {
            return this.dto['description'];
        }

        const typeName = this.type?.name;
        const objectId = this.objectIdentifier?.objectId;

        return typeName ? `${typeName} #${objectId}` : `Objekt #${objectId}`;
    }

    getTypeIcon(): string {
        return this.type?.image || '';
    }

    /** rgbaColor holds a theme variable NAME, never a literal colour. */
    getTypeIconStyle(): { [key: string]: string } {

        const color = this.type?.rgbaColor;

        return color ? {'color': 'var(--' + color + ')'} : {};
    }

    getTypeName(): string {
        return this.type?.name || this.dto?.['typeDtoName'] || '';
    }

    // ---------------------------------------------------------------------------------------- //
    // Kunde
    // ---------------------------------------------------------------------------------------- //

    /**
     * Load the customer assignments of this object.
     *
     * Executed against the service resolved by alias - cr must not be imported at runtime (GC1).
     * The first assignment also supplies the customer the address resolution and the embedded form
     * contexts need, so the address section is resolved from here.
     */
    protected loadAssignments(): void {

        const service: MvsCrudService = this.coreService.getCrudService(CI_OBJECT_TYPE_CUSTOMER_INSURABLE_OBJECT);

        if (!service) {
            this.assignmentsError = 'Der Dienst für Kundenzuordnungen ist nicht verfügbar.';
            return;
        }

        this.assignmentsBusy = true;
        this.assignmentsError = null;

        const request = CiCustomerInsurableObjectRequest.createForInsurableObject(this.objectIdentifier.objectId);

        this.subscription.add(
            service.list(request).subscribe({
                next: (dtoList: DtoList) => {

                    this.assignments = (dtoList?.entries ?? []) as CustomerInsurableObjectDto[];
                    this.assignmentsBusy = false;

                    // Exactly one assignment is the normal case - showing a list of one to click
                    // through would be pure friction.
                    this.selectedAssignment = this.assignments.length === 1 ? this.assignments[0] : null;

                    this.refreshFormImportObjectContext();
                    this.refreshAddress();
                },
                error: () => {
                    this.assignments = [];
                    this.assignmentsBusy = false;
                    this.assignmentsError = 'Die Kundenzuordnungen konnten nicht geladen werden.';

                    // Without the customer the address cannot be classified either.
                    this.refreshAddress();
                }
            })
        );
    }

    handleSelectAssignment(assignment: CustomerInsurableObjectDto): void {
        this.selectedAssignment = assignment;
    }

    handleBackToAssignmentList(): void {
        this.selectedAssignment = null;
    }

    /** The customer this object is bound to - the first assignment wins. */
    getBoundCustomerId(): number {
        return this.assignments?.[0]?.customerDtoId;
    }

    getAssignmentLabel(assignment: CustomerInsurableObjectDto): string {
        return assignment?.['customerDtoName'] || ('Kunde #' + assignment?.customerDtoId);
    }

    getAssignmentTypeLabel(assignment: CustomerInsurableObjectDto): string {

        const assignmentType = assignment?.assignmentType;

        return assignmentType == null ? '' : (ASSIGNMENT_TYPE_LABELS[assignmentType] ?? '');
    }

    /** More than one assignment means the section shows a chooser first. */
    showAssignmentList(): boolean {
        return this.assignments.length > 1 && !this.selectedAssignment;
    }

    // ---------------------------------------------------------------------------------------- //
    // Adresse
    // ---------------------------------------------------------------------------------------- //

    /**
     * Decide whether this object carries an address of its own, and rebuild the rail accordingly.
     */
    protected refreshAddress(): void {

        this.addressBusy = true;

        this.subscription.add(
            CiInsurableObjectAddressResolver.resolve(
                this.addressFieldService, this.coreService, this.dto, this.getBoundCustomerId()
            ).subscribe({
                next: (address: CiInsurableObjectAddress) => {
                    this.address = address;
                    this.addressBusy = false;
                    this.rebuildNavigationItems();
                },
                error: () => {
                    // A failed classification hides the section rather than showing a broken one.
                    this.address = null;
                    this.addressBusy = false;
                    this.rebuildNavigationItems();
                }
            })
        );
    }

    getAddressId(): number {
        return this.address?.addressId;
    }

    // ---------------------------------------------------------------------------------------- //
    // Embedded forms
    // ---------------------------------------------------------------------------------------- //

    /**
     * Merge the ambient context with the bound customer.
     *
     * The backend InsurableObjectForm scopes its dynamic address and person value lists to the
     * customer taken from this context; without the entry the data form would offer every address
     * of the system. Our entry wins over a caller supplied one, and the input context is never
     * mutated.
     */
    protected refreshFormImportObjectContext(): void {

        const customerId = this.getBoundCustomerId();
        const context = this.importObjectContext
            ? this.importObjectContext.copy()
            : DtoImportObjectContext.createEmpty();

        if (customerId) {
            context.addEntryAndReplaceType(new DtoImportObjectContextEntry(OBJECT_TYPE_CUSTOMER, customerId));
        }

        this.formImportObjectContext = context;
    }

    /**
     * The DATA form saved: pass the event on (the object component contract) and re-read the object
     * so header, type and address classification stay in sync.
     *
     * Bound per form rather than switching on event.objectType: that string is resolved lazily from
     * the service registry, and making a visible refresh depend on it would be a silent failure
     * mode the moment a registration changes.
     */
    handleChangedInsurableObject(event: ObjectChangeInformation): void {

        this.onChangedObject.emit(event);

        // Allowed here: this is an event handler, NOT onObjectChanged - no loop.
        this.refreshObject(false);
    }

    /**
     * A change to the ASSIGNMENT does not touch the object, but it can change the bound customer
     * and therefore whether the address still counts as object specific.
     */
    handleChangedAssignment(event: ObjectChangeInformation): void {

        this.onChangedObject.emit(event);
        this.loadAssignments();
    }

    /**
     * The ADDRESS form edits the ad.Address row the object points at. Editing its fields cannot
     * change WHICH address that is, so nothing has to be re-read here.
     */
    handleChangedAddress(event: ObjectChangeInformation): void {
        this.onChangedObject.emit(event);
    }

    /** The address form edits a plain ad.Address row. */
    getAddressObjectType(): string {
        return CI_ADDRESS_OBJECT_TYPE_ALIAS;
    }

    /** The id of the object this component shows - the identifier is the authoritative source. */
    getInsurableObjectId(): number {
        return this.objectIdentifier?.objectId;
    }

    /**
     * Compact mode: hide the delete action on ALL THREE forms of this component - the object data,
     * the customer assignment and the object specific address. Bound to each {@code [removeDelete]}.
     *
     * Read from the {@code params} seam of ObjectBaseComponent, which every host may set. A host
     * that offers this component as an "Angaben ergänzen" surface - the inline strip of
     * ci-customer-insurable-objects - must be able to take deletion off it entirely: that strip
     * exists to COMPLETE missing data, and every delete button on it is an offer the user did not
     * ask for. Sparing the assignment and the address would leave exactly that offer standing one
     * click away in the icon rail, so the suppression covers the whole surface, not just its
     * entry section.
     *
     * Strict === true so an absent params object, and any other truthy payload a host might carry,
     * leave the default behaviour untouched. Every other host therefore keeps delete everywhere.
     */
    get suppressDelete(): boolean {
        return this.params?.removeDelete === true;
    }

    /**
     * Should the icon rail keep its section in the shared {@code sideNavItem} query parameter?
     *
     * Opt-OUT, not opt-in: the default stays the deep-linkable object page behaviour, and only a
     * host that knows its rail is not the page turns it off. The inline strip does, because on that
     * route a SECOND rail is on screen at the same time - the cr.CustomerInsurableObject component
     * in the side panel, whose sections happen to include "address" as well. Sharing one unnamespaced
     * parameter, the two hijack each other: a side panel standing on "Adresse" would open the strip
     * on "Adresse" too, and the missing field the user clicked would not even be visible.
     *
     * `!== false` rather than `=== true`, so hosts that pass an unrelated params payload keep the
     * documented default instead of silently losing their deep links.
     */
    get syncNavigationToUrl(): boolean {
        return this.params?.syncNavigationToUrl !== false;
    }

}
